import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card, Table, Button, Modal, Form, Input, Select, AutoComplete, Tabs, Tag, Space, message,
  Popconfirm, Upload, Typography, Badge, Tooltip, Statistic, Row, Col
} from 'antd';
import {
  PlusOutlined, UploadOutlined, YoutubeOutlined, EditOutlined, DeleteOutlined,
  CheckCircleOutlined, RollbackOutlined, LinkOutlined, BellOutlined, SearchOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import api, { formatApiError } from '../utils/api';

const { Text, Title } = Typography;

export default function VideoTracker({ user }) {
  const isAdmin = ['admin', 'super_admin', 'manager', 'hr_manager'].includes(user?.role);
  const [tab, setTab] = useState('pending');
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ pending: 0, uploaded: 0, total: 0 });
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [channelFilter, setChannelFilter] = useState(null);

  const [addModal, setAddModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [uploadRow, setUploadRow] = useState(null);
  const [editUploadRow, setEditUploadRow] = useState(null);
  const [notifyModal, setNotifyModal] = useState(false);
  const [staff, setStaff] = useState([]);
  const [ftSelected, setFtSelected] = useState([]);
  const [ftAddModal, setFtAddModal] = useState(false);

  const [ftAddForm] = Form.useForm();
  const [addForm] = Form.useForm();
  const [uploadForm] = Form.useForm();
  const [editUploadForm] = Form.useForm();
  const [notifyForm] = Form.useForm();
  const searchTimer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (tab === 'firsttime') params.view = 'firsttime';
      else if (tab === 'list') params.view = 'list';
      else params.status = tab === 'uploaded' ? 'uploaded' : 'pending';
      if (search) params.search = search;
      if (channelFilter) params.channel_id = channelFilter;
      const [listRes, statsRes] = await Promise.all([
        api.get('/video-tracker', { params }),
        api.get('/video-tracker/stats'),
      ]);
      setRows(listRes.data || []);
      setStats(statsRes.data || { pending: 0, uploaded: 0, total: 0 });
    } catch (err) {
      message.error(formatApiError(err, 'Failed to load'));
    } finally {
      setLoading(false);
    }
  }, [tab, search, channelFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/channels').then(r => setChannels(r.data || [])).catch(() => {});
  }, []);

  // ---- Excel import ----
  const handleImport = (file) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        if (!data.length) { message.error('Excel is empty'); return; }
        // Detect header row (first row that has "song")
        let headerIdx = 0;
        for (let i = 0; i < Math.min(3, data.length); i++) {
          if (data[i].some(c => String(c).toLowerCase().includes('song'))) { headerIdx = i; break; }
        }
        const rowsOut = [];
        for (let i = headerIdx + 1; i < data.length; i++) {
          const r = data[i];
          const title = String(r[0] || '').trim();
          if (!title) continue;
          rowsOut.push({
            song_title: title,
            artist: String(r[1] || '').trim(),
            singer: String(r[2] || '').trim(),
            video_shoot: String(r[3] || '').trim(),
            dress_colour: String(r[4] || '').trim(),
            note: String(r[5] || '').trim(),
          });
        }
        if (!rowsOut.length) { message.error('No song rows found in Excel'); return; }
        const res = await api.post('/video-tracker/import', { rows: rowsOut });
        message.success(`Imported ${res.data.added} songs (${res.data.skipped} skipped as duplicate)`);
        load();
      } catch (err) {
        message.error(formatApiError(err, 'Import failed'));
      }
    };
    reader.readAsBinaryString(file);
    return false; // prevent default upload
  };

  // ---- Import historical First-Time-Use data (Song/Singer/Month/Video Shoot/
  //      Video Edit/Channel/Video Link/Artist/Location/Dress) ----
  const findCol = (headers, ...needles) => {
    for (let i = 0; i < headers.length; i++) {
      const h = String(headers[i] || '').toLowerCase().trim();
      if (needles.some(n => h.includes(n))) return i;
    }
    return -1;
  };

  const handleImportFirstTime = (file) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        if (!data.length) { message.error('Excel is empty'); return; }
        let headerIdx = 0;
        for (let i = 0; i < Math.min(4, data.length); i++) {
          if (data[i].some(c => String(c).toLowerCase().includes('song'))) { headerIdx = i; break; }
        }
        const H = data[headerIdx];
        const cSong = findCol(H, 'song', 'title');
        const cSinger = findCol(H, 'singer');
        const cMonth = findCol(H, 'month');
        const cShoot = findCol(H, 'shoot');
        const cEdit = findCol(H, 'edit');
        const cChannel = findCol(H, 'channel');
        const cLink = findCol(H, 'link', 'url');
        const cArtist = findCol(H, 'artist');
        const cLoc = findCol(H, 'location');
        const cDress = findCol(H, 'dress');
        const g = (r, i) => i >= 0 ? String(r[i] ?? '').trim() : '';
        const rowsOut = [];
        for (let i = headerIdx + 1; i < data.length; i++) {
          const r = data[i];
          const title = g(r, cSong >= 0 ? cSong : 0);
          if (!title) continue;
          rowsOut.push({
            song_title: title,
            singer: g(r, cSinger),
            month: g(r, cMonth),
            video_shoot: g(r, cShoot),
            video_edit: g(r, cEdit),
            channel_name: g(r, cChannel),
            youtube_link: g(r, cLink),
            artist: g(r, cArtist),
            location: g(r, cLoc),
            dress_colour: g(r, cDress),
          });
        }
        if (!rowsOut.length) { message.error('No song rows found in Excel'); return; }
        const res = await api.post('/video-tracker/import-firsttime', { rows: rowsOut });
        message.success(`First Time Use: ${res.data.added} added (${res.data.skipped} skipped as duplicate)`);
        setTab('firsttime');
        load();
      } catch (err) {
        message.error(formatApiError(err, 'Import failed'));
      }
    };
    reader.readAsBinaryString(file);
    return false;
  };

  const transferToUploaded = async () => {
    if (!ftSelected.length) return;
    try {
      const res = await api.post('/video-tracker/transfer-uploaded', { ids: ftSelected });
      message.success(`${res.data.transferred} row(s) Uploaded me bhej diye`);
      setFtSelected([]);
      load();
    } catch (err) {
      message.error(formatApiError(err, 'Transfer failed'));
    }
  };

  // ---- Download the import template (same column order the importer reads) ----
  const downloadTemplate = () => {
    const aoa = [
      ['Song', 'Artist', 'Singer', 'Video Shoot', 'Dress Colour', 'Note'],
      ['Kabutar Bole Gutar Gu 012', 'Bainsla Music', 'DG Mauli', 'DJ Shoot', 'Red', ''],
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 28 }, { wch: 18 }, { wch: 16 }, { wch: 16 }, { wch: 14 }, { wch: 16 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Video Tracker');
    XLSX.writeFile(wb, 'video-tracker-format.xlsx');
  };

  const submitAdd = async () => {
    try {
      const vals = await addForm.validateFields();
      if (editRow) {
        await api.put(`/video-tracker/${editRow.id}`, vals);
        message.success('Updated');
      } else {
        await api.post('/video-tracker', vals);
        message.success('Song added to pending');
      }
      setAddModal(false); setEditRow(null); addForm.resetFields();
      load();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(formatApiError(err, 'Save failed'));
    }
  };

  // Resolve typed/selected channel name into { channel_id, channel_name }.
  const resolveChannel = (name) => {
    const nm = (name || '').trim();
    const ch = channels.find(c => (c.channel_name || '').trim().toLowerCase() === nm.toLowerCase());
    return { channel_id: ch ? ch.id : null, channel_name: nm };
  };

  const submitUpload = async () => {
    try {
      const vals = await uploadForm.validateFields();
      const ch = resolveChannel(vals.channel_name);
      const res = await api.post(`/video-tracker/${uploadRow.id}/upload`, {
        channel_id: ch.channel_id,
        channel_name: ch.channel_name,
        video_title: vals.video_title,
        youtube_link: vals.youtube_link,
      });
      message.success(`Uploaded — code ${res.data?.first_time_code || ''} (First Time Use me aa gaya)`);
      setUploadRow(null); uploadForm.resetFields();
      load();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(formatApiError(err, 'Failed'));
    }
  };

  const submitEditUpload = async () => {
    try {
      const vals = await editUploadForm.validateFields();
      const ch = resolveChannel(vals.channel_name);
      await api.put(`/video-tracker/${editUploadRow.id}`, {
        song_title: vals.song_title ?? editUploadRow.song_title,
        artist: vals.artist ?? editUploadRow.artist,
        singer: vals.singer ?? editUploadRow.singer,
        month: vals.month ?? editUploadRow.month,
        video_shoot: vals.video_shoot ?? editUploadRow.video_shoot,
        video_edit: vals.video_edit ?? editUploadRow.video_edit,
        location: vals.location ?? editUploadRow.location,
        dress_colour: vals.dress_colour ?? editUploadRow.dress_colour,
        channel_id: ch.channel_id,
        channel_name: ch.channel_name,
        video_title: vals.video_title,
        youtube_link: vals.youtube_link,
      });
      message.success('Updated');
      setEditUploadRow(null); editUploadForm.resetFields();
      load();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(formatApiError(err, 'Failed'));
    }
  };

  const openEditUpload = (row) => {
    setEditUploadRow(row);
    editUploadForm.setFieldsValue({
      song_title: row.song_title || '',
      singer: row.singer || '',
      artist: row.artist || '',
      month: row.month || '',
      video_shoot: row.video_shoot || '',
      video_edit: row.video_edit || '',
      location: row.location || '',
      dress_colour: row.dress_colour || '',
      channel_name: row.channel_name || '',
      video_title: row.video_title || '',
      youtube_link: row.youtube_link || '',
    });
  };

  const revert = async (id) => {
    try { await api.post(`/video-tracker/${id}/revert`); message.success('Moved back to pending'); load(); }
    catch (err) { message.error(formatApiError(err, 'Failed')); }
  };

  const del = async (id) => {
    try { await api.delete(`/video-tracker/${id}`); message.success('Deleted'); load(); }
    catch (err) { message.error(formatApiError(err, 'Failed')); }
  };

  const openNotify = async () => {
    try {
      const [uRes, nRes] = await Promise.all([
        api.get('/auth/users'),
        api.get('/video-tracker/notify-users'),
      ]);
      setStaff((uRes.data || []).filter(u => !['admin', 'super_admin'].includes(u.role)));
      notifyForm.setFieldsValue({ user_ids: nRes.data || [] });
      setNotifyModal(true);
    } catch (err) { message.error(formatApiError(err, 'Failed')); }
  };

  const saveNotify = async () => {
    try {
      const vals = await notifyForm.validateFields();
      await api.post('/video-tracker/notify-users', { user_ids: vals.user_ids || [] });
      message.success('Notification staff updated');
      setNotifyModal(false);
    } catch (err) { message.error(formatApiError(err, 'Failed')); }
  };

  const submitFtAdd = async () => {
    try {
      const vals = await ftAddForm.validateFields();
      const res = await api.post('/video-tracker/add-firsttime', vals);
      message.success(`First Time Use me add ho gaya (${res.data.first_time_code})`);
      setFtAddModal(false);
      ftAddForm.resetFields();
      load();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(formatApiError(err, 'Add failed'));
    }
  };

  const openEdit = (row) => {
    setEditRow(row);
    addForm.setFieldsValue(row);
    setAddModal(true);
  };

  const pendingCols = [
    { title: 'Song', dataIndex: 'song_title', key: 'song_title', render: t => <b>{t}</b> },
    { title: 'Artist', dataIndex: 'artist' },
    { title: 'Singer', dataIndex: 'singer' },
    { title: 'Video Shoot', dataIndex: 'video_shoot' },
    { title: 'Dress Colour', dataIndex: 'dress_colour' },
    {
      title: 'Action', key: 'action', fixed: 'right', width: 220,
      render: (_, row) => (
        <Space>
          <Button type="primary" size="small" icon={<YoutubeOutlined />} onClick={() => { setUploadRow(row); uploadForm.resetFields(); }}>
            Mark Uploaded
          </Button>
          <Tooltip title="Edit"><Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} /></Tooltip>
          <Popconfirm title="Delete this song?" onConfirm={() => del(row.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const uploadedCols = [
    { title: 'Song', dataIndex: 'song_title', key: 'song_title', render: t => <b>{t}</b> },
    { title: 'Singer', dataIndex: 'singer' },
    { title: 'Channel', dataIndex: 'channel_name', render: c => c ? <Tag color="red">{c}</Tag> : '-' },
    { title: 'Video Title', dataIndex: 'video_title' },
    {
      title: 'Link', dataIndex: 'youtube_link', render: l => l
        ? <a href={l} target="_blank" rel="noreferrer"><LinkOutlined /> Open</a> : '-'
    },
    {
      title: 'Uploaded', dataIndex: 'uploaded_at',
      render: d => d ? <Text type="secondary">{new Date(d).toLocaleDateString('en-IN')}</Text> : '-'
    },
    { title: 'By', dataIndex: 'uploaded_by_name', render: n => n || '-' },
    {
      title: 'Action', key: 'action', fixed: 'right', width: 210,
      render: (_, row) => (
        <Space>
          <Tooltip title="Edit channel / link / title">
            <Button size="small" type="primary" ghost icon={<EditOutlined />} onClick={() => openEditUpload(row)}>Edit</Button>
          </Tooltip>
          <Tooltip title="Move back to Pending">
            <Popconfirm title="Move back to pending?" onConfirm={() => revert(row.id)}>
              <Button size="small" icon={<RollbackOutlined />}>Undo</Button>
            </Popconfirm>
          </Tooltip>
          <Popconfirm title="Delete this record?" onConfirm={() => del(row.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  // Video List = exactly the Excel sheet columns, in the same order.
  const listCols = [
    { title: 'Song', dataIndex: 'song_title', key: 'song_title', width: 300, fixed: 'left', render: t => <b>{t}</b> },
    { title: 'Artist', dataIndex: 'artist', width: 200 },
    { title: 'Singer', dataIndex: 'singer', width: 170 },
    { title: 'Video Shoot', dataIndex: 'video_shoot', width: 160 },
    { title: 'Dress Colur', dataIndex: 'dress_colour', width: 260 },
    { title: 'Note', dataIndex: 'note', width: 150 },
    {
      title: 'Action', key: 'action', fixed: 'right', width: 110,
      render: (_, row) => (
        <Space>
          <Tooltip title="Edit details"><Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} /></Tooltip>
          <Popconfirm title="Delete this video record?" onConfirm={() => del(row.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const firstTimeCols = [
    { title: 'Code', dataIndex: 'first_time_code', key: 'code', width: 80, fixed: 'left', render: c => c ? <Tag color="purple" style={{ fontWeight: 'bold', fontSize: 13 }}>{c}</Tag> : '-' },
    { title: 'Song', dataIndex: 'song_title', key: 'song_title', width: 240, fixed: 'left', render: t => <b>{t}</b> },
    { title: 'Singer', dataIndex: 'singer', width: 150 },
    { title: 'Artist', dataIndex: 'artist', width: 150 },
    { title: 'Month', dataIndex: 'month', width: 90 },
    { title: 'Video Shoot', dataIndex: 'video_shoot', width: 120 },
    { title: 'Video Edit', dataIndex: 'video_edit', width: 120 },
    { title: 'Channel', dataIndex: 'channel_name', width: 170, render: c => c ? <Tag color="red">{c}</Tag> : '-' },
    {
      title: 'Link', dataIndex: 'youtube_link', width: 90, render: l => l
        ? <a href={l} target="_blank" rel="noreferrer"><LinkOutlined /> Open</a> : '-'
    },
    { title: 'Location', dataIndex: 'location', width: 140 },
    { title: 'Dress', dataIndex: 'dress_colour', width: 120 },
    { title: 'By', dataIndex: 'uploaded_by_name', width: 120, render: n => n || '-' },
    {
      title: 'Action', key: 'action', fixed: 'right', width: 100,
      render: (_, row) => (
        <Space>
          <Tooltip title="Edit channel / link / title">
            <Button size="small" type="primary" ghost icon={<EditOutlined />} onClick={() => openEditUpload(row)}>Edit</Button>
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}><Card size="small"><Statistic title="Pending (Not Uploaded)" value={stats.pending} valueStyle={{ color: '#faad14' }} prefix={<UploadOutlined />} /></Card></Col>
        <Col xs={12} sm={6}><Card size="small"><Statistic title="First Time Use" value={stats.firsttime || 0} valueStyle={{ color: '#722ed1' }} prefix={<YoutubeOutlined />} /></Card></Col>
        <Col xs={12} sm={6}><Card size="small"><Statistic title="Uploaded to YouTube" value={stats.uploaded} valueStyle={{ color: '#52c41a' }} prefix={<CheckCircleOutlined />} /></Card></Col>
        <Col xs={12} sm={6}><Card size="small"><Statistic title="Total Songs" value={stats.total} prefix={<YoutubeOutlined />} /></Card></Col>
      </Row>

      <Card
        title={<Title level={4} style={{ margin: 0 }}><YoutubeOutlined /> Video Upload Tracker</Title>}
        extra={
          <Space wrap>
            <Input
              allowClear placeholder="Search song / channel / singer" prefix={<SearchOutlined />}
              onChange={(e) => {
                const v = e.target.value;
                if (searchTimer.current) clearTimeout(searchTimer.current);
                searchTimer.current = setTimeout(() => setSearch(v), 400);
              }}
              style={{ width: 220 }}
            />
            <Select
              allowClear placeholder="Filter by channel" style={{ width: 180 }}
              value={channelFilter} onChange={setChannelFilter}
              showSearch optionFilterProp="children"
            >
              {channels.map(c => <Select.Option key={c.id} value={c.id}>{c.channel_name}</Select.Option>)}
            </Select>
            {isAdmin && <Button icon={<BellOutlined />} onClick={openNotify}>Notify Staff</Button>}
            <Button onClick={downloadTemplate}>Excel Format</Button>
            <Upload accept=".xlsx,.xls,.csv" showUploadList={false} beforeUpload={handleImport}>
              <Button icon={<UploadOutlined />}>Import Pending</Button>
            </Upload>
            <Upload accept=".xlsx,.xls,.csv" showUploadList={false} beforeUpload={handleImportFirstTime}>
              <Button icon={<UploadOutlined />} style={{ borderColor: '#722ed1', color: '#722ed1' }}>Import First Time Use</Button>
            </Upload>
            <Button icon={<PlusOutlined />} style={{ borderColor: '#722ed1', color: '#722ed1' }}
              onClick={() => { ftAddForm.resetFields(); setFtAddModal(true); }}>Add First Time Use</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditRow(null); addForm.resetFields(); setAddModal(true); }}>Add Video</Button>
          </Space>
        }
      >
        <Tabs
          activeKey={tab} onChange={setTab}
          items={[
            { key: 'pending', label: <Badge count={stats.pending} offset={[10, 0]} color="#faad14">Pending&nbsp;&nbsp;</Badge> },
            { key: 'firsttime', label: <Badge count={stats.firsttime || 0} offset={[10, 0]} color="#722ed1">First Time Use&nbsp;&nbsp;</Badge> },
            { key: 'uploaded', label: <Badge count={stats.uploaded} offset={[10, 0]} color="#52c41a">Uploaded&nbsp;&nbsp;</Badge> },
            { key: 'list', label: <Badge count={stats.all || 0} offset={[10, 0]} color="#1677ff">Video List&nbsp;&nbsp;</Badge> },
          ]}
        />
        {tab === 'firsttime' && (
          <Space style={{ marginBottom: 8 }}>
            <Button
              type="primary"
              disabled={!ftSelected.length}
              onClick={transferToUploaded}
            >
              Uploaded me bhejo{ftSelected.length ? ` (${ftSelected.length})` : ''}
            </Button>
            <span style={{ color: '#888' }}>Select karke Uploaded me bhejo — dono jagah rahega</span>
          </Space>
        )}
        {tab === 'list' && (
          <Space style={{ marginBottom: 8 }} wrap>
            <Text type="secondary">Excel sheet ki poori list — jaisi upload ki gayi thi.</Text>
          </Space>
        )}
        <Table
          rowKey="id" loading={loading}
          columns={tab === 'pending' ? pendingCols : tab === 'firsttime' ? firstTimeCols : tab === 'list' ? listCols : uploadedCols}
          dataSource={rows}
          rowSelection={tab === 'firsttime' ? { selectedRowKeys: ftSelected, onChange: setFtSelected } : undefined}
          scroll={{ x: tab === 'firsttime' ? 1700 : tab === 'list' ? 1350 : 900 }}
          pagination={{ pageSize: 50, showSizeChanger: true }}
          size="small"
        />
      </Card>

      {/* Add / Edit song */}
      <Modal
        title={editRow ? 'Edit Video' : 'Add Video'} open={addModal}
        onOk={submitAdd} onCancel={() => { setAddModal(false); setEditRow(null); }}
        okText="Save"
      >
        <Form form={addForm} layout="vertical">
          <Form.Item name="song_title" label="Song / Number" rules={[{ required: true, message: 'Song title required' }]}>
            <Input placeholder="e.g. Kabutar Bole Gutar Gu 012" />
          </Form.Item>
          <Form.Item name="artist" label="Artist"><Input /></Form.Item>
          <Form.Item name="singer" label="Singer"><Input placeholder="e.g. DG Mauli" /></Form.Item>
          <Form.Item name="video_shoot" label="Video Shoot"><Input placeholder="e.g. Dj Shoot / Park Shoot" /></Form.Item>
          <Form.Item name="dress_colour" label="Dress Colur"><Input placeholder="e.g. Dark Blue Top & Lehanga" /></Form.Item>
          <Form.Item name="note" label="Note"><Input placeholder="e.g. Holi Song" /></Form.Item>
        </Form>
      </Modal>

      {/* Add a single First-Time-Use record (all columns) */}
      <Modal
        title={<span><PlusOutlined /> Add First Time Use</span>}
        open={ftAddModal} onOk={submitFtAdd} onCancel={() => setFtAddModal(false)} okText="Save" width={720}
      >
        <Form form={ftAddForm} layout="vertical">
          <Row gutter={12}>
            <Col span={8}><Form.Item name="first_time_code" label="Code (khali chhodo to khud BM number lag jayega)"><Input placeholder="BM123" /></Form.Item></Col>
            <Col span={16}><Form.Item name="song_title" label="Song" rules={[{ required: true, message: 'Song title required' }]}><Input /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="singer" label="Singer"><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="artist" label="Artist"><Input /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={8}><Form.Item name="month" label="Month"><Input placeholder="e.g. Oct 2025" /></Form.Item></Col>
            <Col span={8}><Form.Item name="video_shoot" label="Video Shoot"><Input /></Form.Item></Col>
            <Col span={8}><Form.Item name="video_edit" label="Video Edit"><Input /></Form.Item></Col>
          </Row>
          <Form.Item name="channel_id" label="Channel (list me se select karo)">
            <Select allowClear showSearch optionFilterProp="children" placeholder="Channel choose karo">
              {channels.map(c => <Select.Option key={c.id} value={c.id}>{c.channel_name}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="youtube_link" label="Link"><Input placeholder="https://youtube.com/watch?v=..." /></Form.Item>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="location" label="Location"><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="dress_colour" label="Dress"><Input /></Form.Item></Col>
          </Row>
        </Form>
      </Modal>

      {/* Mark uploaded */}
      <Modal
        title={<span><YoutubeOutlined style={{ color: '#f00' }} /> Mark Uploaded — {uploadRow?.song_title}</span>}
        open={!!uploadRow} onOk={submitUpload} onCancel={() => setUploadRow(null)} okText="Mark Uploaded"
      >
        <Form form={uploadForm} layout="vertical">
          <Form.Item name="channel_name" label="Channel (select karo ya naam type karo)" rules={[{ required: true, message: 'Channel choose or type karo' }]}>
            <AutoComplete
              showSearch allowClear filterOption={(input, opt) => (opt?.value || '').toLowerCase().includes(input.toLowerCase())}
              options={channels.map(c => ({ value: c.channel_name }))}
              placeholder="Apna channel select karo ya naam likho"
            />
          </Form.Item>
          <Form.Item name="youtube_link" label="YouTube Link" rules={[{ required: true, message: 'Paste the YouTube link' }]}>
            <Input placeholder="https://youtube.com/watch?v=..." />
          </Form.Item>
          <Form.Item name="video_title" label="Video Title (optional)">
            <Input placeholder="Chaho to video title daalo" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit uploaded record (channel / link / title) — stays uploaded */}
      <Modal
        title={<span><EditOutlined /> Edit Upload — {editUploadRow?.song_title}</span>}
        open={!!editUploadRow} onOk={submitEditUpload} onCancel={() => setEditUploadRow(null)} okText="Save"
      >
        <Form form={editUploadForm} layout="vertical">
          <Form.Item name="song_title" label="Song / Title" rules={[{ required: true, message: 'Song title required' }]}>
            <Input />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="singer" label="Singer"><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="artist" label="Artist"><Input /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={8}><Form.Item name="month" label="Month"><Input placeholder="e.g. Oct" /></Form.Item></Col>
            <Col span={8}><Form.Item name="video_shoot" label="Video Shoot"><Input /></Form.Item></Col>
            <Col span={8}><Form.Item name="video_edit" label="Video Edit"><Input /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="location" label="Location"><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="dress_colour" label="Dress"><Input /></Form.Item></Col>
          </Row>
          <Form.Item name="channel_name" label="Channel (select karo ya naam type karo)" rules={[{ required: true, message: 'Channel choose or type karo' }]}>
            <AutoComplete
              showSearch allowClear filterOption={(input, opt) => (opt?.value || '').toLowerCase().includes(input.toLowerCase())}
              options={channels.map(c => ({ value: c.channel_name }))}
              placeholder="Apna channel select karo ya naam likho"
            />
          </Form.Item>
          <Form.Item name="youtube_link" label="YouTube Link" rules={[{ required: true, message: 'Paste the YouTube link' }]}>
            <Input placeholder="https://youtube.com/watch?v=..." />
          </Form.Item>
          <Form.Item name="video_title" label="Video Title (optional)">
            <Input placeholder="Chaho to video title daalo" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Notify staff (admin) */}
      <Modal
        title={<span><BellOutlined /> Staff who see new-upload notifications (7 days)</span>}
        open={notifyModal} onOk={saveNotify} onCancel={() => setNotifyModal(false)} okText="Save"
      >
        <Text type="secondary">Selected staff will see uploaded songs on their dashboard for 7 days.</Text>
        <Form form={notifyForm} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item name="user_ids" label="Select staff">
            <Select mode="multiple" showSearch optionFilterProp="children" placeholder="Choose staff">
              {staff.map(u => <Select.Option key={u.id} value={u.id}>{u.full_name || u.username} ({u.role})</Select.Option>)}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
